import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

interface Booking {
  id: string;
  guestName: string;
  propertyName: string;
  location: string;
  checkIn: Date;
  checkOut: Date;
  status: 'upcoming' | 'in-progress' | 'completed';
  guestImage: string;
  rating?: number;
}

const mockBookings: Booking[] = [
  {
    id: '1',
    guestName: 'Sarah Johnson',
    propertyName: 'Luxury Villa',
    location: 'Bali, Indonesia',
    checkIn: new Date(2024, 3, 15),
    checkOut: new Date(2024, 3, 20),
    status: 'upcoming',
    guestImage: 'https://picsum.photos/200/200?random=1',
    rating: 4.8
  },
  {
    id: '2',
    guestName: 'Michael Chen',
    propertyName: 'Beachfront Suite',
    location: 'Cancun, Mexico',
    checkIn: new Date(2024, 3, 18),
    checkOut: new Date(2024, 3, 22),
    status: 'upcoming',
    guestImage: 'https://picsum.photos/200/200?random=2',
    rating: 4.9
  },
  {
    id: '3',
    guestName: 'Emma Davis',
    propertyName: 'Mountain Cabin',
    location: 'Aspen, Colorado',
    checkIn: new Date(2024, 3, 12),
    checkOut: new Date(2024, 3, 16),
    status: 'in-progress',
    guestImage: 'https://picsum.photos/200/200?random=3',
    rating: 5.0
  }
];

const BookingsWidget: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Upcoming Bookings</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your upcoming reservations</p>
        </div>
        <button className="inline-flex items-center px-4 py-2 bg-airbnb-red text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors duration-200 shadow-sm">
          View Calendar
        </button>
      </div>

      <div className="space-y-4">
        {mockBookings.map((booking, index) => (
          <motion.div
            key={booking.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex items-start p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group cursor-pointer"
          >
            <img
              src={booking.guestImage}
              alt={booking.guestName}
              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
            />
            
            <div className="ml-4 flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 group-hover:text-airbnb-red transition-colors">
                    {booking.guestName}
                  </h3>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" />
                    {booking.location}
                  </p>
                </div>
                <div className="flex items-center">
                  <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                  <span className="text-sm font-medium text-gray-600">{booking.rating}</span>
                </div>
              </div>

              <div className="mt-2 flex items-center text-sm text-gray-500">
                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                <span>
                  {format(booking.checkIn, 'MMM d')} - {format(booking.checkOut, 'MMM d')}
                </span>
                <span className={`
                  ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${booking.status === 'in-progress' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-blue-100 text-blue-800'
                  }
                `}>
                  {booking.status === 'in-progress' ? 'In Progress' : 'Upcoming'}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <button className="mt-6 w-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium py-3 rounded-xl transition-colors flex items-center justify-center">
        <CalendarIcon className="h-5 w-5 mr-2" />
        View All Bookings
      </button>
    </motion.div>
  );
};

export default BookingsWidget;